import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { EMPTY, Observable, throwError } from 'rxjs';
import { AuthService } from '../http/auth/auth.service';
import { ErrorConstants } from 'src/app/shared/models/errorConstants';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { GlobalService } from '../services/global.service';
import { publicRoutes } from 'src/app/shared/models/constants';
import { I18nService } from '../services/i18n.service';
import { QuickEnrollSessionService } from '../services/quick-enroll-session.service';
import { GlobalConstants } from 'src/app/shared/models/globalConstants';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    lastUrl = '';
    constructor(
        private token: AuthService,
        private router: Router,
        private toastr: ToastrService,
        private global: GlobalService,
        private i18n: I18nService,
        private qeSession: QuickEnrollSessionService,
    ) { }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        let authReq = request;
        const token = this.token.getToken();
        const existingLang = request.headers.get('Accept-Language');
        const lang = existingLang ?? (request.url.includes('/quick-enroll/') ? this.i18n.currentLang : 'en');
        if (token != null) {
            authReq = request.clone({
                headers: request.headers
                    .set(ErrorConstants.TOKEN_HEADER_KEY, 'Bearer ' + token)
                    .set('Accept-Language', lang),
            });
        } else {
            authReq = request.clone({
                headers: request.headers.set('Accept-Language', lang),
            });
        }
        /* IES-5562: Quick Enroll APIs use the Quick Enroll session token with the employer / billing schedule it was issued for. */
        const session = this.qeSession.isProtectedUrl(request.url) ? this.qeSession.get() : null;
        /* IES-5562: a logged-in user (login token, no Quick Enroll session) calling a shared API keeps the login behaviour. */
        const isQuickEnrollApi = this.qeSession.isProtectedUrl(request.url) && (!!session || token == null);
        if (session) {
            authReq = authReq.clone({
                headers: authReq.headers
                    .set(ErrorConstants.TOKEN_HEADER_KEY, 'Bearer ' + session.token)
                    .set(GlobalConstants.QE_EMPLOYER_HEADER, session.employerNo)
                    .set(GlobalConstants.QE_BILLING_SCHEDULE_HEADER, session.billingScheduleNo),
            });
        }
        return next.handle(authReq).pipe(
            catchError((err: HttpErrorResponse) => {
                const url = localStorage.getItem('url');
                const loginUrl = localStorage.getItem('loginUrl');

                this.lastUrl = url?.trim() ? url
                            : loginUrl?.trim() ? loginUrl
                            : '/login';
                /* IES-5562: an expired or mismatched Quick Enroll session restarts Quick Enroll at the identity step, the page tells the user after the reload. */
                if (isQuickEnrollApi && [401, 403].includes(err.status)) {
                    this.qeSession.clear(true);
                    location.reload();
                    return EMPTY;
                }
                if (err.error.message)
                    this.toastr.warning(err.error.message);
                if ([401, 440].includes(err.status)) {
                    this.token.deleteToken();
                    if (publicRoutes.includes(this.lastUrl)) {
                        localStorage.removeItem('url');
                        this.router.navigateByUrl(this.lastUrl);
                    } else {
                        this.router.navigateByUrl(`/login`);
                    }

                    /* IES-2967: After session expiration, the page reloads infinitely */
                    if (440 === err.status)
                        return EMPTY;

                    location.reload();
                } else {
                    this.global.loaderStop();
                    return throwError(err);
                }
                this.global.loaderStop();
            })
        );
    }
}
